import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceIdentification } from '../../IdentifyDevice.types';
import { DeviceIdentificationForm } from '../DeviceIdentificationForm/DeviceIdentificationForm';
import { BarcodeScannerFooter } from './BarcodeScannerFooter';
import { StyledBarcodeScanner } from './BarcodeScanner.styles';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { toast } from 'app/cross-cutting-concerns/toasts/kcc/toast';
import { parseBarcode } from 'app/modules/service/utils/device';
import { BARCODE_SCANNER_FILE_READER_ID, BARCODE_SCANNER_READER_ID } from 'app/modules/service/utils/constants';
import { BarcodeScannerModal } from 'app/modules/service/modals/BarcodeScannerModal/BarcodeScannerModal';

interface BarcodeScannerProps {
  onEnterManually: () => void;
  onValidityChange: (isValid: boolean) => void;
}

const SCANNER_CONFIG = {
  verbose: false,
  useBarCodeDetectorIfSupported: true,
  formatsToSupport: [Html5QrcodeSupportedFormats.ITF, Html5QrcodeSupportedFormats.QR_CODE],
};

export const BarcodeScanner = ({ onEnterManually, onValidityChange }: BarcodeScannerProps): JSX.Element => {
  const { t } = useTranslation();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const isProcessingRef = useRef(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [scannedDevice, setScannedDevice] = useState<DeviceIdentification | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const stopCamera = async (): Promise<void> => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
      } catch {
        // ignore stop errors
      }
      scannerRef.current = null;
    }
    setIsCameraOpen(false);
    setPermissionDenied(false);
  };

  const handleScanResult = async (decodedText: string): Promise<void> => {
    const parsed = parseBarcode(decodedText);
    await stopCamera();

    if (parsed) {
      setScannedDevice(parsed);
      onValidityChange(true);
      toast.success({
        message: t('service.identifyDevice.barcodeScanner.recognised'),
        placement: 'bottomRight',
      });
    } else {
      toast.error({
        message: t('service.identifyDevice.barcodeScanner.notRecognised'),
        placement: 'bottomRight',
      });
    }
  };

  const handleAfterOpenChange = async (open: boolean): Promise<void> => {
    if (!open) return;

    isProcessingRef.current = false;

    const onScanSuccess = async (decodedText: string): Promise<void> => {
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;
      await handleScanResult(decodedText);
    };

    const scanner = new Html5Qrcode(BARCODE_SCANNER_READER_ID, SCANNER_CONFIG);
    scannerRef.current = scanner;

    try {
      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: (w: number, h: number) => {
            const size = Math.round(Math.min(w, h) * 0.6);
            return { width: size, height: size };
          },
        },
        onScanSuccess,
        undefined
      );
    } catch (err) {
      const isPermissionDenied = typeof err === 'string' && err.includes('NotAllowedError');
      toast.error({
        message: t(
          isPermissionDenied
            ? 'service.identifyDevice.barcodeScanner.cameraPermissionDenied'
            : 'service.identifyDevice.barcodeScanner.scanFailed'
        ),
        placement: 'bottomRight',
      });
      scannerRef.current = null;
      if (isPermissionDenied) {
        setPermissionDenied(true);
      } else {
        setIsCameraOpen(false);
      }
    }
  };

  const handleGalleryFileChange = async (file: File): Promise<void> => {
    const fileScanner = new Html5Qrcode(BARCODE_SCANNER_FILE_READER_ID, SCANNER_CONFIG);
    try {
      const decodedText = await fileScanner.scanFile(file, false);
      isProcessingRef.current = true;
      await handleScanResult(decodedText);
    } catch {
      toast.error({
        message: t('service.identifyDevice.barcodeScanner.notRecognised'),
        placement: 'bottomRight',
      });
    }
  };

  const handleEnterManually = (): void => {
    onValidityChange(false);
    onEnterManually();
  };

  return (
    <StyledBarcodeScanner className="barcode-scanner">
      {scannedDevice ? (
        <DeviceIdentificationForm
          initialValues={scannedDevice}
          onValidityChange={onValidityChange}
          footer={<BarcodeScannerFooter onEnterManually={handleEnterManually} />}
        />
      ) : (
        <>
          <div className="barcode-scanner__card">
            <SvgIcon name="qrCode" className="barcode-scanner__icon" />
            <p className="barcode-scanner__title">{t('service.identifyDevice.barcodeScanner.title')}</p>
            <div>
              <p className="barcode-scanner__description">
                {t('service.identifyDevice.barcodeScanner.descriptionLine1')}
              </p>
              <p className="barcode-scanner__description">
                {t('service.identifyDevice.barcodeScanner.descriptionLine2')}
              </p>
            </div>
            <KccButton className="barcode-scanner__scan-btn" type="primary" onClick={(): void => setIsCameraOpen(true)}>
              {t('service.identifyDevice.barcodeScanner.scanButton')}
            </KccButton>
          </div>

          <span className="barcode-scanner__or">{t('common.or')}</span>

          <KccButton className="barcode-scanner__manual-btn" type="secondary" onClick={handleEnterManually}>
            {t('service.identifyDevice.barcodeScanner.manualButton')}
          </KccButton>

          <BarcodeScannerModal
            open={isCameraOpen}
            onClose={stopCamera}
            afterOpenChange={handleAfterOpenChange}
            permissionDenied={permissionDenied}
            onGalleryFileChange={handleGalleryFileChange}
          />
        </>
      )}
    </StyledBarcodeScanner>
  );
};
